import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  ShieldCheck, ShieldAlert, ShieldOff, Wallet, ArrowLeft, Loader2, CheckCircle2, XCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const METHOD_LABELS = {
  cash: "Dinheiro", pix: "PIX", bank: "Transferência", card: "Cartão",
};

function fmtCurrency(v, currency) {
  const symbol = { BRL: "R$", USD: "$", EUR: "€", HTG: "G" }[currency] || (currency || "");
  const n = Number(v || 0);
  return `${symbol} ${n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function VerifyReceipt() {
  const { receiptNumber } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError("");
    axios.get(`${API}/verify/${receiptNumber}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setError("Não foi possível verificar o recibo. Tente novamente."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [receiptNumber]);

  const isValid = data?.valid;
  const isCancelled = data && data.status === "cancelled";
  const notFound = data && !data.valid && !isCancelled;

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-white to-primary/5 flex flex-col"
         data-testid="verify-page">
      {/* Top bar */}
      <header className="border-b border-border bg-white/70 backdrop-blur">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-md bg-primary/10 border border-primary/30 grid place-items-center">
              <Wallet className="h-5 w-5 text-primary" strokeWidth={2.2} />
            </div>
            <div>
              <p className="font-display font-bold text-base tracking-tight">
                {data?.company_name || "JMS ERP"}
              </p>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Verificação Pública de Documento
              </p>
            </div>
          </div>
          <Button asChild variant="ghost" size="sm" data-testid="verify-home-link">
            <Link to="/login" className="text-muted-foreground hover:text-primary">
              <ArrowLeft className="h-4 w-4 mr-1" /> Área do sistema
            </Link>
          </Button>
        </div>
      </header>

      <main className="flex-1 max-w-4xl w-full mx-auto px-6 py-10 md:py-14">
        {loading && (
          <div className="rounded-2xl border border-border bg-white p-10 grid place-items-center text-muted-foreground"
               data-testid="verify-loading">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <p className="mt-3 text-sm">Verificando autenticidade…</p>
          </div>
        )}

        {!loading && error && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center" data-testid="verify-error">
            <ShieldAlert className="h-10 w-10 text-rose-600 mx-auto" />
            <h1 className="mt-3 font-display text-2xl font-semibold text-rose-800">Erro de verificação</h1>
            <p className="mt-2 text-sm text-rose-700">{error}</p>
          </div>
        )}

        {!loading && !error && data && (
          <div className="space-y-6" data-testid="verify-content">
            {/* Result banner */}
            <div className={
              `rounded-2xl border p-6 sm:p-8 flex items-start gap-5 ${
                isValid ? "border-primary/30 bg-primary/5"
                : isCancelled ? "border-amber-200 bg-amber-50"
                : "border-rose-200 bg-rose-50"
              }`
            } data-testid="verify-banner">
              <div className={
                `h-14 w-14 rounded-full grid place-items-center shrink-0 ${
                  isValid ? "bg-primary text-white"
                  : isCancelled ? "bg-amber-500 text-white"
                  : "bg-rose-600 text-white"
                }`
              }>
                {isValid ? <ShieldCheck className="h-7 w-7" strokeWidth={2.2} />
                 : isCancelled ? <ShieldOff className="h-7 w-7" strokeWidth={2.2} />
                 : <XCircle className="h-7 w-7" strokeWidth={2.2} />}
              </div>
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Status do documento</p>
                <h1 className="font-display text-2xl sm:text-3xl font-semibold mt-0.5"
                    data-testid="verify-title">
                  {isValid ? "Documento autêntico" : isCancelled ? "Documento cancelado" : "Documento não encontrado"}
                </h1>
                <p className="mt-1 text-sm text-muted-foreground max-w-xl">
                  {isValid && "Este recibo foi emitido pelo sistema e é válido para fins de comprovação de pagamento."}
                  {isCancelled && "Este número existe, porém o documento foi cancelado. Não deve ser considerado válido."}
                  {notFound && "Não encontramos nenhum recibo com este número. Verifique se digitou corretamente."}
                </p>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className="rounded-md font-mono text-xs"
                         data-testid="verify-receipt-number">
                    {data.receipt_number}
                  </Badge>
                  {isValid && (
                    <Badge className="rounded-md bg-primary/10 text-primary border-primary/30">
                      <CheckCircle2 className="h-3 w-3 mr-1" /> Emitido
                    </Badge>
                  )}
                  {isCancelled && (
                    <Badge className="rounded-md bg-amber-100 text-amber-800 border-amber-300">
                      Cancelado
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Details */}
            {(isValid || isCancelled) && (
              <div className="rounded-2xl border border-border bg-white overflow-hidden">
                <div className="px-6 py-4 border-b border-border">
                  <h2 className="font-display font-semibold text-base">Detalhes do documento</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Estas informações foram registradas no momento da emissão.
                  </p>
                </div>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 px-6 py-6 text-sm">
                  <Field label="Número do recibo" value={data.receipt_number} testid="detail-receipt" mono />
                  <Field label="Tipo" value={data.type === "receipt" ? "Recibo de pagamento" : data.type} testid="detail-type" />
                  <Field label="Cliente" value={data.customer_name} testid="detail-customer" />
                  <Field label="Serviço" value={data.service_name} testid="detail-service" />
                  <Field label="Valor pago" value={fmtCurrency(data.amount_paid, data.currency)} testid="detail-amount" mono highlight />
                  <Field label="Data do pagamento" value={data.payment_date || "—"} testid="detail-payment-date" />
                  <Field label="Método" value={METHOD_LABELS[data.payment_method] || data.payment_method || "—"} testid="detail-method" />
                  <Field label="Emitido em" value={fmtDateTime(data.issued_at)} testid="detail-issued-at" />
                </dl>
                <div className="px-6 py-4 border-t border-border bg-muted/40">
                  <p className="text-xs text-muted-foreground">Emissor</p>
                  <p className="text-sm font-medium">{data.company_name}</p>
                  {(data.company_legal_name || data.cnpj) && (
                    <p className="text-xs text-muted-foreground">
                      {data.company_legal_name}{data.cnpj ? ` · CNPJ ${data.cnpj}` : ""}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      <footer className="border-t border-border">
        <div className="max-w-4xl mx-auto px-6 py-4 text-[11px] text-muted-foreground flex items-center justify-between">
          <span>© {new Date().getFullYear()} {data?.company_name || "JMS ERP"} — Todos os direitos reservados.</span>
          <span>Página pública de verificação</span>
        </div>
      </footer>
    </div>
  );
}

function Field({ label, value, testid, mono = false, highlight = false }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</dt>
      <dd data-testid={testid}
          className={`mt-1 ${mono ? "font-mono" : ""} ${highlight ? "text-primary font-semibold text-base" : "text-foreground"}`}>
        {value || "—"}
      </dd>
    </div>
  );
}
